/* eslint-disable react/no-unstable-nested-components -- DataTable `cell` callbacks are called as plain functions, never mounted as components. */
import { AlertTriangle, CheckCircle2, Crosshair, XCircle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import type { DropCatchAttempt, Drop } from '../../../api/areas/market';
import { Callout, DataTable, Section, StatCard, StatGrid, type Column } from '../../../components/blocks';
import { DataError } from '../../../components/DataError';
import { FactRows, GateList } from '../../../components/flow';
import { DomainLink } from '../../../components/Ids';
import { PageHeader } from '../../../components/PageHeader';
import { EmptyState, Loading } from '../../../components/States';
import { formatCount, formatDateTime } from '../../../lib/format';
import { hubUrl } from '../../../nav/hubs';
import { RefreshButton } from '../../registry/shared';
import { BackorderLinks, counted, useBackorderRows } from '../module';
import { CATCH_STATUS, checkResult, CustomerRef, MarketBadge, money } from '../shared';

type Row = DropCatchAttempt & { drop: Drop };

const RUNNING: DropCatchAttempt['status'][] = ['PLANNED', 'POLLING', 'REGISTERING'];

/** `/backorders?tab=catching`, reference 62: every catch attempt, per registry, and how a catch is decided. */
export function CatchingPage() {
  const { t } = useTranslation('market');
  const { drops, reload } = useBackorderRows();
  const header = <PageHeader title={t('bo.catching.title')} subtitle={t('bo.catching.subtitle')} actions={<RefreshButton onClick={reload} />} />;
  if (drops.status !== 'success') {
    return (
      <>
        {header}
        {drops.status === 'error' ? <DataError error={drops.error} onRetry={reload} title={t('bo.loadError')} /> : <Loading rows={5} />}
      </>
    );
  }
  const ds = drops.data.items;
  const rows: Row[] = ds.flatMap((d) => (d.attempt ? [{ ...d.attempt, drop: d }] : []));
  const n = (f: (r: Row) => boolean) => counted(ds, rows.filter(f).length);
  const byRegistry = new Map<string, Row[]>();
  for (const r of rows) byRegistry.set(r.drop.registry_name ?? '—', [...(byRegistry.get(r.drop.registry_name ?? '—') ?? []), r]);

  const columns: Column<Row>[] = [
    {
      key: 'domain',
      header: t('backorders.domain'),
      cell: (r) => (
        <div className="space-y-0.5">
          <Link to={`/drops/${encodeURIComponent(r.drop_id)}`} className="font-semibold text-primary hover:underline">
            {r.domain_name}
          </Link>
          <div className="text-xs text-muted-foreground">{r.drop.registry_name ?? '—'}</div>
        </div>
      ),
    },
    { key: 'for', header: t('backorders.for'), className: 'max-w-44 whitespace-normal', cell: (r) => <CustomerRef id={r.customer_id} name={r.customer_name} sub={money(r.amount, r.currency)} /> },
    { key: 'window', header: t('backorder.window'), className: 'whitespace-nowrap text-xs', cell: (r) => `${formatDateTime(r.window_start)} → ${formatDateTime(r.window_end)}` },
    { key: 'status', header: t('backorders.status'), cell: (r) => <MarketBadge group="catchStatus" map={CATCH_STATUS} value={r.status} /> },
    {
      key: 'checks',
      header: t('backorder.checks'),
      numeric: true,
      cell: (r) => (
        <div className="space-y-0.5">
          <div>{formatCount(r.checks_sent)}</div>
          {r.last_check_result ? <div className="text-xs whitespace-normal text-muted-foreground">{checkResult(r.last_check_result)}</div> : null}
        </div>
      ),
    },
    {
      key: 'result',
      header: t('bo.catching.result'),
      className: 'max-w-56 whitespace-normal',
      cell: (r) =>
        r.domain_id ? (
          <DomainLink keyOrName={r.domain_name} label={t('bo.catching.openDomain')} />
        ) : r.order_id ? (
          <Link to={`/orders/${encodeURIComponent(r.order_id)}`} className="text-primary hover:underline">
            {t('bo.catching.openOrder')}
          </Link>
        ) : (
          <span className="text-xs text-muted-foreground">{r.outcome_reason ?? '—'}</span>
        ),
    },
  ];

  return (
    <>
      {header}
      <BackorderLinks here="catching" />
      <div className="space-y-5">
        <StatGrid>
          <StatCard label={t('bo.catching.running')} icon={Crosshair} tone="info" value={n((r) => RUNNING.includes(r.status))} hint={t('bo.catching.runningHint')} />
          <StatCard label={t('bo.catching.caught')} icon={CheckCircle2} tone="ok" value={n((r) => r.status === 'CAUGHT')} hint={t('bo.catching.caughtHint')} />
          <StatCard label={t('bo.catching.notCaught')} icon={XCircle} value={n((r) => r.status === 'NOT_CAUGHT')} hint={t('bo.catching.notCaughtHint')} />
          <StatCard label={t('bo.catching.review')} icon={AlertTriangle} tone={rows.some((r) => r.status === 'MANUAL_REVIEW') ? 'crit' : 'neutral'} value={n((r) => r.status === 'MANUAL_REVIEW')} hint={t('bo.catching.reviewHint')} />
        </StatGrid>
        <div className="grid grid-cols-1 items-start gap-5 xl:grid-cols-[minmax(0,1fr)_380px] [&>*]:min-w-0">
          <div className="min-w-0 space-y-5">
            <Section flush title={t('bo.catching.attempts')} description={t('bo.catching.attemptsHint')}>
              <DataTable columns={columns} rows={rows} rowKey={(r) => r.id} empty={<EmptyState title={t('bo.catching.none')} />} />
            </Section>
            <Section title={t('bo.catching.registries')} description={t('bo.catching.registriesHint')}>
              {byRegistry.size ? (
                <FactRows
                  rows={[...byRegistry.entries()].map(([name, list]) => [
                    name,
                    t('bo.catching.registryLine', {
                      running: list.filter((r) => RUNNING.includes(r.status)).length,
                      caught: list.filter((r) => r.status === 'CAUGHT').length,
                      failed: list.filter((r) => r.status === 'NOT_CAUGHT').length,
                    }),
                  ])}
                />
              ) : (
                <p className="text-sm text-muted-foreground">{t('bo.catching.none')}</p>
              )}
              <p className="mt-3 text-xs text-muted-foreground">
                {t('bo.catching.capacity')}{' '}
                <Link to={hubUrl('settings', 'connections', 'registries')} className="text-primary hover:underline">
                  {t('bo.catching.capacityLink')}
                </Link>
              </p>
            </Section>
          </div>
          <Section title={t('bo.catching.rulesTitle')} description={t('bo.catching.rulesHint')}>
            <GateList
              gates={[
                { id: 'window', title: t('bo.catching.rule.window'), hint: t('bo.catching.rule.windowHint'), ok: null },
                { id: 'register', title: t('bo.catching.rule.register'), hint: t('bo.catching.rule.registerHint'), ok: null },
                { id: 'capture', title: t('bo.catching.rule.capture'), hint: t('bo.catching.rule.captureHint'), ok: null },
                { id: 'unknown', title: t('bo.catching.rule.unknown'), hint: t('bo.catching.rule.unknownHint'), ok: null },
              ]}
            />
          </Section>
        </div>
        <Callout tone="info">{t('bo.catching.note')}</Callout>
      </div>
    </>
  );
}
